// ---------------------------------------------------------------------------
// C96: Proposal List — filter by status/type, sort, actions
// ---------------------------------------------------------------------------
import { useState } from "react";
import { Link } from "react-router";
import { useProposals, type OfferType } from "@/hooks/use-proposals";
import { formatCurrency, formatDate } from "@/lib/i18n";
import { FileText, Search, Eye, Loader2, DollarSign, Clock, CheckCircle, XCircle, ArrowRight, Send } from "lucide-react";

const STATUS_BADGE: Record<string, { icon: typeof CheckCircle; className: string }> = {
  draft: { icon: FileText, className: "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]" },
  sent: { icon: Send, className: "bg-[var(--color-primary)]/10 text-[var(--color-primary)]" },
  viewed: { icon: Eye, className: "bg-[var(--color-ai)]/10 text-[var(--color-ai)]" },
  signed: { icon: CheckCircle, className: "bg-[var(--color-success)]/10 text-[var(--color-success)]" },
  declined: { icon: XCircle, className: "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]" },
  expired: { icon: Clock, className: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]" },
};

const PAYMENT_BADGE: Record<string, string> = {
  unpaid: "text-[var(--color-muted-foreground)]",
  partial: "text-[var(--color-warning)]",
  paid: "text-[var(--color-success)]",
};

const OFFER_TYPES: OfferType[] = ["proposal", "quote", "invoice", "package", "estimate"];

export default function ProposalListPage() {
  const [status, setStatus] = useState("");
  const [offerType, setOfferType] = useState("");
  const [search, setSearch] = useState("");
  const { proposals, isLoading } = useProposals({
    status: status || undefined,
    offerType: offerType || undefined,
    search: search || undefined,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Proposals & Offers</h1>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{proposals.length} total</p>
        </div>
        <Link to="/services/estimates" className="btn-press inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-white" style={{ background: "var(--gradient-energy)" }}>
          <ArrowRight className="h-4 w-4" /> New Estimate
        </Link>
      </div>

      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-muted)]/20 px-4 py-3 text-sm text-[var(--color-muted-foreground)]">
        This mounted surface is the canonical proposal index on the CRM runtime. Draft editing, AI generation, signature collection, and payment links land in the later gated proposal slices, while new estimate creation already lives on
        {" "}
        <Link to="/services/estimates" className="font-medium text-[var(--color-primary)] hover:underline">Services → Estimates</Link>.
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-muted-foreground)]" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search proposals..."
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] pl-10 pr-4 py-2.5 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2.5 text-sm text-[var(--color-foreground)]">
          <option value="">All statuses</option>
          {Object.keys(STATUS_BADGE).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={offerType} onChange={(e) => setOfferType(e.target.value)}
          className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2.5 text-sm text-[var(--color-foreground)]">
          <option value="">All types</option>
          {OFFER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[var(--color-primary)]" /></div>
      ) : proposals.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-[var(--color-border)] p-16 text-center">
          <FileText className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)]/30" />
          <p className="mt-3 text-sm text-[var(--color-muted-foreground)]">No proposals yet.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-[var(--color-border)] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-muted)]/30 text-xs text-[var(--color-muted-foreground)]">
                <th className="px-4 py-3 text-left font-medium">Proposal</th>
                <th className="px-4 py-3 text-left font-medium">Type</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-right font-medium">Value</th>
                <th className="px-4 py-3 text-center font-medium">Views</th>
                <th className="px-4 py-3 text-left font-medium">Payment</th>
                <th className="px-4 py-3 text-right font-medium">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {proposals.map((p) => {
                const badge = STATUS_BADGE[p.status] ?? { icon: FileText, className: "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]" };
                const BadgeIcon = badge.icon;
                return (
                  <tr key={p.id} className="hover:bg-[var(--color-muted)]/20 transition-colors">
                    <td className="px-4 py-3">
                      <span className="font-medium text-[var(--color-foreground)]">{p.title}</span>
                      <p className="text-[10px] text-[var(--color-muted-foreground)]">{p.personName}</p>
                    </td>
                    <td className="px-4 py-3 text-[var(--color-muted-foreground)] capitalize text-xs">{p.offerType}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded ${badge.className}`}>
                        <BadgeIcon className="h-2.5 w-2.5" /> {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium tabular-nums text-[var(--color-foreground)]">
                      {p.totalValue != null
                        ? formatCurrency(Math.round(Number(p.totalValue) * 100), p.currency, { maximumFractionDigits: 0 })
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="flex items-center justify-center gap-0.5 text-xs text-[var(--color-muted-foreground)]">
                        <Eye className="h-3 w-3" /> {p.viewCount}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`flex items-center gap-0.5 text-xs font-medium ${PAYMENT_BADGE[p.paymentStatus]}`}>
                        <DollarSign className="h-3 w-3" /> {p.paymentStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-[var(--color-muted-foreground)]">
                      {formatDate(p.createdAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
